import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS, CONTENT_DOCS } from "@/lib/firebase/firestore";
import type {
  HomepageContent,
  Experience,
  Amenity,
  Testimonial,
  GalleryImage,
} from "@/types/content";

// ─── Homepage ─────────────────────────────────────────────────────────────────

export async function getHomepageContent(): Promise<HomepageContent | null> {
  const snap = await getDoc(
    doc(db, COLLECTIONS.CONTENT, CONTENT_DOCS.HOMEPAGE),
  );
  if (!snap.exists()) return null;
  return snap.data() as HomepageContent;
}

export async function updateHomepageContent(
  data: Partial<HomepageContent>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.CONTENT, CONTENT_DOCS.HOMEPAGE), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

// ─── Experiences ──────────────────────────────────────────────────────────────

export async function getExperiences(): Promise<Experience[]> {
  const q = query(
    collection(db, "experiences"),
    where("active", "==", true),
    orderBy("sortOrder", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Experience);
}

// ─── Amenities ────────────────────────────────────────────────────────────────

export async function getAmenities(): Promise<Amenity[]> {
  const q = query(
    collection(db, "amenities"),
    where("active", "==", true),
    orderBy("sortOrder", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Amenity);
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

export async function getTestimonials(): Promise<Testimonial[]> {
  const q = query(
    collection(db, "testimonials"),
    where("active", "==", true),
    orderBy("sortOrder", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Testimonial);
}

// ─── Gallery ──────────────────────────────────────────────────────────────────

export async function getGalleryImages(
  category?: GalleryImage["category"],
): Promise<GalleryImage[]> {
  const constraints = [
    where("active", "==", true),
    orderBy("sortOrder", "asc"),
  ];
  if (category) constraints.unshift(where("category", "==", category));

  const q = query(collection(db, "gallery"), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GalleryImage);
}

// ─── Gallery: batch update sort orders after drag-and-drop ───────────────────

export async function batchUpdateGallerySortOrder(
  items: { id: string; sortOrder: number }[],
): Promise<void> {
  const batch = writeBatch(db);
  items.forEach(({ id, sortOrder }) => {
    batch.update(doc(db, "gallery", id), {
      sortOrder,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}